import type { ReactNode } from 'react';
import type { FetchLike } from '@dpem/contracts';
import { ToastProvider } from '@dpem/design-system';
import type { QueryClient } from '@tanstack/react-query';

import { ApiProvider } from './ApiProvider';
import { AuthProvider } from './AuthProvider';

/**
 * Every provider the app needs, in dependency order, in one place.
 *
 * Order is not cosmetic: `AuthProvider` calls the api client and the query cache,
 * so it must sit inside `ApiProvider`. `ToastProvider` is outermost so a failure
 * raised during session restore still has somewhere to be announced.
 *
 * The router is *not* here. Tests render a surface inside a `MemoryRouter` while
 * the app uses a browser router, and coupling the two would mean the test helper
 * could not reuse this component.
 */

export interface AppProvidersProps {
  children: ReactNode;
  /** Test seams. Production passes none of these. */
  fetch?: FetchLike;
  baseUrl?: string;
  queryClient?: QueryClient;
  now?: () => number;
  restoreOnMount?: boolean;
}

export function AppProviders({
  children,
  fetch,
  baseUrl,
  queryClient,
  now,
  restoreOnMount,
}: AppProvidersProps) {
  return (
    <ToastProvider placement="bottom-right">
      <ApiProvider fetch={fetch} baseUrl={baseUrl} queryClient={queryClient}>
        <AuthProvider now={now} restoreOnMount={restoreOnMount}>
          {children}
        </AuthProvider>
      </ApiProvider>
    </ToastProvider>
  );
}
